import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";

import { I18nextLanguage, LinkType } from "@/types/types";

dayjs.extend(duration);
dayjs.extend(relativeTime);

// eslint-disable-next-line @typescript-eslint/no-require-imports
const { getDayJSLocale } = require("@/utils/functions.js");

/**
 * Test for production and development environments
 */
export const isProduction = process.env.NODE_ENV === "production";
export const isVercelProduction =
  process.env.NEXT_PUBLIC_VERCEL_ENV === "production";
export const isDevelopment = process.env.NODE_ENV === "development";

/**
 * Get random item from array
 */
export function getRandomItemFromArray<T>(array: T[]): T {
  const randomIndex = Math.floor(Math.random() * array.length);
  return array[randomIndex];
}

/**
 * Sort array or objects by key
 */
export function sortByKey<T>(
  array: T[],
  key: string,
  sortOrder: "asc" | "desc" = "asc"
): T[] {
  return [...array].sort(function (a, b) {
    const x = a[key];
    const y = b[key];
    if (x < y) return sortOrder === "asc" ? -1 : 1;
    if (x > y) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });
}

/**
 * Determine whether a string is a pure number
 */
export function isStringNumber(str: string): boolean {
  return !isNaN(parseFloat(str));
}

/**
 * Determine whether string is timestamp
 */
export function isTimestamp(n: string | number): boolean {
  const nString = typeof n === "number" ? n.toString() : n;
  const nFloat = typeof n === "number" ? n : parseFloat(n);
  return (
    !Number.isNaN(nFloat) &&
    Number.isFinite(nFloat) &&
    /^\d+\.?\d+$/.test(nString)
  );
}

/**
 * Determine whether string is a valid date defintion
 */
export function isDate(testString: string): boolean {
  const dateParsed = Date.parse(testString);
  return isTimestamp(dateParsed);
}

/**
 * Test link type
 */
export function testLinkType(href: string): LinkType {
  if (href.startsWith("/")) return "internal";
  if (href.startsWith("mailto:")) return "email";
  return "external";
}

/**
 *
 * Capitilises the first letter of each word in a string
 *
 * @param str - The string to be capitilised
 * @returns The capitilised string
 *
 * @example
 * toTitleCase("dr. greve") // "Dr. Greve"
 */
export function toTitleCase(str: string): string {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Get natural language description of how long ago a date was (eg "1 day ago", "2 months ago").
 * Calculates the period based on the length of the time diff.
 * Accepts either a duration (in milliseconds)
 * or a date (in milliseconds) which is compared to "now"
 */
export function getTimeAgoLength({
  duration,
  date,
  locale,
}: { locale: I18nextLanguage } & (
  | {
      duration: number;
      date?: never;
    }
  | {
      duration?: never;
      date: number | string;
    }
)): string {
  const _duration = duration || dayjs().diff(dayjs(date), "millisecond");
  return dayjs
    .duration(-1 * _duration)
    .locale(getDayJSLocale(locale))
    .humanize(true);
}

/**
 * HANDLE LOCAL STATE
 *
 */

// SET
export const setLocalStorage = (
  key: string,
  value: string | Record<string, unknown>,
  ttl?: number // optional time to live in seconds
) => {
  // Format item
  const now = new Date();
  const item = {
    value,
    expiry: ttl ? now.getTime() + ttl * 1000 : null,
  };
  try {
    localStorage.setItem(key, JSON.stringify(item));
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

// GET
export const getLocalStorage = <T>(key: string): T => {
  const item = localStorage.getItem(key);
  if (!item) return null;
  const { value, expiry } = JSON.parse(item);
  // If expiry is defined and is in the past, remove item
  const now = new Date();
  if (now.getTime() > expiry) {
    // If the item is expired, delete the item from storage
    // and return null
    clearLocalStorage(key);
    return null;
  }
  return value;
};

// CLEAR
export const clearLocalStorage = (key?: string) => {
  try {
    if (key) {
      setLocalStorage(key, "");
    } else {
      localStorage.clear();
    }
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};
export const getObjectKeys = function (obj) {
  const keys = Object.keys(obj);
  const length = keys.length;

  if (length !== 0) {
    for (let i = 0; i < length; i++) {
      if (typeof obj[keys[i]] === "object" && obj[keys[i]] !== null) {
        keys[keys[i]] = getObjectKeys(obj[keys[i]]);
      }
    }
  }

  return keys;
};
